/**
 * ENG-5684 — one row of the staff feed. Live twin of the prototype's
 * `FulfillmentPassiveFeed/FeedRow`: same six columns, same classes.
 *
 * Three deliberate differences from the prototype:
 *
 *  - The project name is the only click target, and it is a `Link`. The
 *    prototype made the whole `<tr>` a `role="link"` with a nested `<button>`
 *    inside it — two overlapping targets, and a button on a surface that must
 *    offer no call to action (ENG-5683).
 *  - No self-invite. The prototype's footnote said opening a row logs the visit
 *    into the engagement's thread; ENG-5677 shipped the opposite — OF staff read
 *    every trilateral invisibly, and speaking is what reveals them.
 *  - The status badge renders as `of_fulfillment`, so `pending_confirmation`
 *    reads "Awaiting customer accept" rather than "Awaiting your accept".
 */
import { Link } from 'react-router';

import Avatar from '@/auth/components/Avatar';

import { formatRelative } from '../components/EngagementHub/format';
import StaffRedFlagDots from './StaffRedFlagDots';
import StatusBadge from './StatusBadge';
import type { StaffFeedRow as Row } from './staffFeedViewModel';

const CELL = 'px-12 py-12 align-top';

interface StaffFeedRowProps {
  row: Row;
}

export default function StaffFeedRow({ row }: StaffFeedRowProps): JSX.Element {
  return (
    <tr className="border-b border-surface-border transition-colors last:border-none focus-within:bg-surface-secondary hover:bg-surface-secondary">
      <td className={CELL}>
        <div className="flex min-w-[180px] flex-col gap-2">
          <span className="text-on-surface-muted util-caption">{row.customerName}</span>
          <Link
            to={`/engagements/${row.engagementId}`}
            className="text-left font-medium text-on-surface body-2"
          >
            {row.projectName}
          </Link>
        </div>
      </td>
      <td className={CELL}>
        <div className="flex items-center gap-8">
          {row.expertHasProfile ? (
            <Avatar fullName={row.expertName} sx={{ width: 28, height: 28, fontSize: 12 }} />
          ) : (
            <div
              className="flex h-[28px] w-[28px] shrink-0 items-center justify-center rounded-full border border-dashed border-surface-border bg-surface-elevated text-on-surface-muted util-caption"
              aria-hidden
            >
              ?
            </div>
          )}
          <span className="whitespace-nowrap text-on-surface body-2">{row.expertName}</span>
        </div>
      </td>
      <td className={CELL}>
        <StatusBadge status={row.status} awaiting={row.awaiting} viewerRole="of_fulfillment" />
      </td>
      <td className={CELL}>
        <NteCell row={row} />
      </td>
      <td className={CELL}>
        <StaffRedFlagDots flags={row.flags} billingFailureReason={row.billingFailureReason} />
      </td>
      <td className={CELL}>
        <span className="whitespace-nowrap text-on-surface-muted body-2">
          {row.lastActivityIso ? formatRelative(row.lastActivityIso) : '—'}
        </span>
      </td>
    </tr>
  );
}

function NteCell({ row }: { row: Row }) {
  if (!row.hasOffer) {
    return <span className="text-on-surface-muted body-2">—</span>;
  }
  return (
    <div className="flex min-w-[120px] flex-col gap-2">
      <span className="tabular-nums text-on-surface body-2">
        {row.nteConsumed ?? 0}h / {row.nteTotal}h
      </span>
      {row.status === 'active' && row.nteConsumedPct !== null ? (
        <span className="text-on-surface-muted util-caption">{row.nteConsumedPct}% consumed</span>
      ) : null}
    </div>
  );
}
